/**
 * GitLab React Query configurations and hooks
 */

import {
  queryOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';

import {
  addRepositoryWebhook,
  getRepositoryWebhookStatus,
  listGitlabRepositories,
  removeRepositoryWebhook,
} from './repositories';
import type { GitlabRepositoryListRequest } from '../../../../types/gitlab';

export const gitlabRepositoryQueries = {
  all: () => ['gitlab-repositories'],
  byProject: (projectId: string) => [
    ...gitlabRepositoryQueries.all(),
    projectId,
  ],
  list: (params: GitlabRepositoryListRequest) =>
    queryOptions({
      queryKey: [
        ...gitlabRepositoryQueries.byProject(params.projectId),
        'list',
        params,
      ],
      queryFn: () => listGitlabRepositories(params),
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    }),
  webhookStatus: (projectId: string, repositoryId: number) =>
    queryOptions({
      queryKey: [
        ...gitlabRepositoryQueries.byProject(projectId),
        'webhook',
        repositoryId,
      ],
      queryFn: () => getRepositoryWebhookStatus({ projectId, repositoryId }),
      staleTime: 30 * 1000, // 30 seconds
      gcTime: 2 * 60 * 1000, // 2 minutes
    }),
} as const;

/**
 * Mutation hooks for webhook management
 */

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useAddRepositoryWebhook = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: addRepositoryWebhook,
    onSuccess: (data, variables) => {
      // Invalidate repository list to update webhook status
      queryClient.invalidateQueries({
        queryKey: gitlabRepositoryQueries.byProject(variables.projectId),
      });

      // Update webhook status cache
      queryClient.setQueryData(
        gitlabRepositoryQueries.webhookStatus(
          variables.projectId,
          variables.repositoryId,
        ).queryKey,
        { webhook: data.webhook },
      );
    },
  });
};

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
export const useRemoveRepositoryWebhook = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: removeRepositoryWebhook,
    onSuccess: (data, variables) => {
      // Invalidate repository list to update webhook status
      queryClient.invalidateQueries({
        queryKey: gitlabRepositoryQueries.byProject(variables.projectId),
      });

      // Update webhook status cache to null
      queryClient.setQueryData(
        gitlabRepositoryQueries.webhookStatus(
          variables.projectId,
          variables.repositoryId,
        ).queryKey,
        { webhook: null },
      );
    },
  });
};
